import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Users, Mail, Phone, Calendar, FileText } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REQ = { withCredentials: true };

const PassengerManifest = () => {
  const { flightId, date } = useParams();
  const navigate = useNavigate();
  const [flight, setFlight] = useState(null);
  const [manifest, setManifest] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchManifest();
  }, [flightId, date]);

  const fetchManifest = async () => {
    setLoading(true);
    try {
      // Fetch flight details
      const flightRes = await axios.get(`${API}/admin/offline-flights/${flightId}`);
      if (flightRes.data.success) {
        setFlight(flightRes.data.data);
      }

      // Fetch manifest
      const manifestRes = await axios.get(`${API}/admin/offline-flights/${flightId}/manifest/${date}`);
      if (manifestRes.data.success) {
        setManifest(manifestRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching manifest:', error);
      alert('Failed to fetch passenger manifest');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/offline-flights/${flightId}/manifest/${date}/csv`,
        { ...REQ, responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `manifest_${flightId}_${date}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download manifest');
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/offline-flights/${flightId}/manifest/${date}/pdf`,
        { ...REQ, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `manifest_${flightId}_${date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#659A9B] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading manifest...</p>
        </div>
      </div>
    );
  }

  if (!flight) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <p className="text-gray-600">Flight not found</p>
          <button
            onClick={() => navigate('/admin/offline-inventory')}
            className="mt-4 text-[#659A9B] hover:text-[#1B375B]"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const inventory = flight.inventory.find(inv => inv.date === date);
  const adultCount = manifest.filter(p => p.passenger_type === 'adult').length;
  const childCount = manifest.filter(p => p.passenger_type === 'child').length;
  const infantCount = manifest.filter(p => p.passenger_type === 'infant').length;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <button
            onClick={() => navigate(`/admin/offline-inventory/${flightId}/inventory`)}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1B375B] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Inventory
          </button>

          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-[#1B375B]">Passenger Manifest</h1>
              <p className="text-gray-600 mt-1">
                {flight.airline_name} {flight.flight_number} • {flight.route.from_airport} → {flight.route.to_airport}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Date: {date} | PNR: {inventory?.pnr}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={downloadPDF}
                className="flex items-center gap-2 px-4 py-2 bg-[#1B375B] hover:bg-[#659A9B] text-white rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4" />
                Download PDF
              </button>
              <button
                type="button"
                onClick={downloadCSV}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                Download CSV
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">Total Passengers</p>
              <p className="text-2xl font-bold text-blue-900">{manifest.length}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600 font-medium">Adults</p>
              <p className="text-2xl font-bold text-green-900">{adultCount}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-600 font-medium">Children</p>
              <p className="text-2xl font-bold text-purple-900">{childCount}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm text-orange-600 font-medium">Infants</p>
              <p className="text-2xl font-bold text-orange-900">{infantCount}</p>
            </div>
          </div>
        </div>

        {/* Manifest Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {manifest.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No bookings for this date yet</p>
              <p className="text-sm text-gray-400 mt-2">Passenger details will appear here once bookings are made</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">No.</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Booking ID</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Passenger Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Contact</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Booking Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {manifest.map((passenger, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-700">{index + 1}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-mono text-sm text-gray-900">{passenger.booking_id}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-gray-900">{passenger.passenger_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            passenger.passenger_type === 'adult'
                              ? 'bg-blue-100 text-blue-700'
                              : passenger.passenger_type === 'child'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-purple-100 text-purple-700'
                          }`}
                        >
                          {passenger.passenger_type.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-3 h-3" />
                            <span>{passenger.email}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-3 h-3" />
                            <span>{passenger.phone}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-3 h-3" />
                          <span>{new Date(passenger.booking_date).toLocaleDateString()}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            passenger.booking_status === 'confirmed'
                              ? 'bg-green-100 text-green-700'
                              : passenger.booking_status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {passenger.booking_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PassengerManifest;
